/* eslint-disable import/order */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { useParams } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { CancelToken } from 'axios';
import * as R from 'ramda';

// Context
import { AuthContext } from 'authentication/AuthContext';

// Components
import { NoAuthorizationsAvailable } from 'feature/loading-management/shared-components';
import Filters from '../../filters/Filters.connector';
import MobileFilters from '../../filters/MobileFilters';
import Selector from './selector/Selector.connector';
import SelectorsList from '../../selectors-list/SelectorsList';
import SelectorSkeleton from './selector/SelectorSkeleton';
import SelectorVirtualizer from '../../selectors-list/SelectorVirtualizer';

// Utils
import { useSetNoAuthCard, useSetSelectorIndex, useURLRouting } from 'hooks';
import { CARRIER_AUTHORIZATION_VIEW_BY_OPTIONS, sidebarStyles } from '../../utils/const';

// constants can be initialized outside of component body
const SELECTOR_SKELETON_HEIGHT = 80; // actual height of the rendered selector container

const SelectorsListStyled = styled(SelectorsList)(({ theme }) => ({
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  height: '100%',
  overflowY: 'scroll',
  padding: theme.spacing(0, 0, 4.5),
  scrollbarWidth: 'none',
}));

const Sidebar = ({
  cardsFetchError,
  createSnack,
  currentSelection,
  fetchCarrierAuthorizations,
  fetchUniqueSelectors,
  selectorsFetchError,
  selectorsList,
  selectorsListPending,
  setCarrierAuthorizationsPending,
  setFilters,
  setHeaderSubtitle,
  setSelectorsListPending,
  submittingBatch,
}) => {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });

  const { viewBy, viewById } = useParams();

  const { currentAffiliate } = useContext(AuthContext);

  const fetchUniqueSelectorsToken = useRef();
  const selectorsCancelToken = useRef();
  const previousSelectorFetch = useRef();

  const [refetch, setRefetch] = useState(false);

  // this will updated the currentSelection when the user hits the back button
  useURLRouting(currentSelection, selectorsList, viewById, setFilters);

  const itemNameProperty = viewBy === 'carrier' ? 'carrierName' : 'nameShort';
  useSetSelectorIndex(selectorsList, setFilters, itemNameProperty);

  useSetNoAuthCard(selectorsList, selectorsListPending, setCarrierAuthorizationsPending, refetch);

  // function to handle pending state when changing views
  const onChangeViewBy = () => {
    setSelectorsListPending(true);
    setCarrierAuthorizationsPending(true);
  };

  // step one: once an affiliate is selected, fire selectors fetch
  useEffect(() => {
    if (currentAffiliate?.customerIdName && viewBy && !submittingBatch) {
      previousSelectorFetch.current = null;
      if (fetchUniqueSelectorsToken.current?.cancel) {
        fetchUniqueSelectorsToken.current.cancel();
        // this bool is so that if we come in and refetch
        // the selectors we don't show the no availaible
        // card in the cards panel
        setRefetch(true);
      }
      const cancelToken = CancelToken.source();
      fetchUniqueSelectorsToken.current = cancelToken;
      fetchUniqueSelectors(
        {
          supplier: {
            companyCustomerIdName: currentAffiliate.customerIdName,
            dataCompanyId: currentAffiliate.dataCompanyId,
          },
        },
        viewBy,
        cancelToken.token
      )
        // Fetch is a promise if we get some data back
        // set the refetch to false so that the cards
        // panel will render correctly
        .then(res => res?.length && setRefetch(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAffiliate?.customerIdName, currentAffiliate?.dataCompanyId, submittingBatch, viewBy]);

  // step three: once a current selection is found or selected, fire cards fetch
  useEffect(() => {
    const params = {
      supplier: {
        companyCustomerIdName: currentAffiliate?.customerIdName,
        dataCompanyId: currentAffiliate?.dataCompanyId,
      },
      [`${viewBy}Id`]: currentSelection[`${viewBy}Id`],
    };
    const adjustedItemName = currentSelection.carrierName || currentSelection.nameShort;

    if (adjustedItemName && previousSelectorFetch.current !== adjustedItemName) {
      previousSelectorFetch.current = adjustedItemName;
      if (selectorsCancelToken.current?.cancel) {
        selectorsCancelToken.current.cancel();
      }
      const cancelToken = CancelToken.source();
      selectorsCancelToken.current = cancelToken;
      fetchCarrierAuthorizations(params, viewBy, cancelToken.token);
    }
    viewBy === 'carrier' && currentSelection && setHeaderSubtitle(currentSelection?.fein);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelection.carrierName, currentSelection.nameShort]);

  // error handling for both selectors and cards fetch errors
  useEffect(() => {
    const error = cardsFetchError || selectorsFetchError;
    if (error) {
      createSnack({
        message: error,
        options: {
          variant: 'error',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsFetchError, selectorsFetchError]);

  return (
    <Box {...sidebarStyles}>
      {!lgUp ? (
        <MobileFilters>
          <Filters
            loading={selectorsListPending}
            onChangeViewBy={onChangeViewBy}
            viewByOptions={CARRIER_AUTHORIZATION_VIEW_BY_OPTIONS}
          />
        </MobileFilters>
      ) : (
        <Filters
          loading={selectorsListPending}
          onChangeViewBy={onChangeViewBy}
          viewByOptions={CARRIER_AUTHORIZATION_VIEW_BY_OPTIONS}
        />
      )}
      {!lgUp && !selectorsListPending && R.isEmpty(selectorsList) ? (
        <Box p={3}>
          <NoAuthorizationsAvailable />
        </Box>
      ) : (
        <SelectorsListStyled
          loading={selectorsListPending}
          selector={
            <SelectorVirtualizer
              itemSize={() => 80}
              overscanCount={10}
              selectorComponent={Selector}
              selectorsData={selectorsList}
            />
          }
          skeletonComponent={<SelectorSkeleton skeletonHeight={SELECTOR_SKELETON_HEIGHT} />}
          skeletonHeight={SELECTOR_SKELETON_HEIGHT}
        />
      )}
    </Box>
  );
};

Sidebar.propTypes = {
  cardsFetchError: string,
  createSnack: func.isRequired,
  currentSelection: shape({}).isRequired,
  fetchCarrierAuthorizations: func.isRequired,
  fetchUniqueSelectors: func.isRequired,
  selectorsFetchError: string,
  selectorsList: arrayOf(shape({})).isRequired,
  selectorsListPending: bool.isRequired,
  setCarrierAuthorizationsPending: func.isRequired,
  setFilters: func.isRequired,
  setHeaderSubtitle: func.isRequired,
  setSelectorsListPending: func.isRequired,
  submittingBatch: bool.isRequired,
};

Sidebar.defaultProps = {
  cardsFetchError: '',
  selectorsFetchError: '',
};

export default Sidebar;
